// App.js
import "./App.css";
import { useState, useRef } from "react";
import Navbar from "./App/Components/Navbar";
import HomePage from "./App/Screens/Homepage";
import EntityAnalysis from "./App/Screens/EntityAnalysis";
import LoginPage from "./App/Screens/LoginScreen";
import { dt } from "./App/Utils/Untitled-1";

function App() {
  const [search, setSearch] = useState("");
  const [screen, setScreen] = useState(-1);
  const [activeTab, setActiveTab] = useState("core");

  // Central analysis state
  const [analysis, setAnalysis] = useState({
    loading: false,
    error: null,
    data: null,
    clientName: "",
  });

  // Track the current API request so we can cancel it
  const abortControllerRef = useRef(null);

  const handleSearchSubmit = (value) => {
    const trimmed = (value || "").trim();
    const clientName = trimmed; // default if user leaves blank

    // If a previous request is in-flight, cancel it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Move to analysis screen
    setScreen(1);
    setActiveTab("core");
    setSearch(trimmed);

    // Reset + start loading
    setAnalysis({
      loading: true,
      error: null,
      data: null,
      clientName,
    });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchAnalysis = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
            "X-Client": clientName.toLowerCase(),
          },
          body: JSON.stringify({
            client_name: clientName,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        // Expected shape:
        // {
        //   success: true,
        //   answer: "{ ... JSON string ... }"
        // }
        if (!result || !result.answer) {
          throw new Error("Invalid API response structure");
        }

        let parsed;
        try {
          parsed = result.answer;
        } catch (e) {
          console.error("Failed to parse `answer` JSON:", e);
          throw new Error("Unable to parse analysis response");
        }

        // Only update state if this request wasn't aborted
        setAnalysis({
          loading: false,
          error: null,
          data: parsed,
          clientName: clientName || parsed.client_id || "",
        });
      } catch (err) {
        // If request was deliberately aborted (e.g. user went back home), do nothing
        if (err.name === "AbortError") {
          console.log("Analysis request aborted");
          return;
        }

        console.error("Analysis error:", err);
        setAnalysis((prev) => ({
          ...prev,
          loading: false,
          error:
            "Something went wrong while analyzing this client. Please try again.",
          data: null,
        }));

        // setAnalysis({
        //   loading: false,
        //   error: null,
        //   data: dt.answer,
        //   clientName: clientName,
        // });
      } finally {
        // Clear controller reference if this was the active one
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    };

    fetchAnalysis();
  };

  const handleHome = () => {
    // Cancel in-flight API call if user goes back to home
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setScreen(0);
    setActiveTab("core");
    // Ensure we’re not stuck in loading state when going home
    setAnalysis((prev) => ({
      ...prev,
      loading: false,
    }));
  };

  return (
    <>
      {screen === -1 && (
        <LoginPage
          ToHome={() => {
            setScreen(0);
          }}
        />
      )}
      {screen >= 0 && (
        <>
          <Navbar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            screen={screen}
            onHome={handleHome}
          />

          {screen === 0 && (
            <HomePage
              searchValue={search}
              onSearchChange={setSearch}
              onSearchSubmit={handleSearchSubmit}
              isAnalyzing={analysis.loading}
            />
          )}

          {screen === 1 && (
            <EntityAnalysis
              ActiveTab={activeTab}
              analysis={analysis}
              ToHome={() => {
                setScreen(0);
              }}
            />
          )}
        </>
      )}
    </>
  );
}

export default App;
